/**
 * `settings` store —— 网络 / 隐私 / 存储 / 锁定策略。
 *
 * 持久化：非敏感的 `theme` 走 localStorage 白名单项 `meshlink.theme`；
 * 其余一律走 IndexedDB `settings` store（ARCH §8.4.7）。
 */

import { create } from 'zustand';
import { container } from '@/services/container';
import { LS_KEYS } from '@/config';
import { DEFAULT_AUTO_LOCK_MINUTES } from '@/config';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store:settings');

export type Theme = 'dark' | 'light';
export type RelayPreference = 'auto' | 'direct' | 'relay';

export interface SettingsState {
  theme: Theme;
  stealth: boolean;
  readReceipts: boolean;
  autoLockMinutes: number;
  cacheLimitMB: number;
  blockedWords: string[];
  directOnly: boolean;
  relayPreference: RelayPreference;
  hydrated: boolean;

  hydrate(): Promise<void>;
  setTheme(theme: Theme): void;
  toggleStealth(): void;
  setReadReceipts(value: boolean): void;
  setAutoLockMinutes(minutes: number): void;
  setCacheLimitMB(mb: number): void;
  setBlockedWords(words: string[]): void;
  setDirectOnly(value: boolean): void;
  setRelayPreference(value: RelayPreference): void;
  reset(): void;
}

const DEFAULT_STATE = {
  theme: 'dark' as Theme,
  stealth: false,
  readReceipts: true,
  autoLockMinutes: DEFAULT_AUTO_LOCK_MINUTES,
  cacheLimitMB: 500,
  blockedWords: [] as string[],
  directOnly: false,
  relayPreference: 'auto' as RelayPreference,
};

/** 把设置写入 IndexedDB（失败只记日志，不影响使用）。 */
async function persist(key: string, value: unknown): Promise<void> {
  try {
    if (!container.storage.ready) return;
    await container.storage.settings.set(key, value);
  } catch (err) {
    logger.warn('设置持久化失败', { key, err });
  }
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  ...DEFAULT_STATE,
  hydrated: false,

  hydrate: async () => {
    try {
      const stored = localStorage.getItem(LS_KEYS.theme);
      if (stored === 'dark' || stored === 'light') set({ theme: stored });
    } catch {
      // 忽略
    }
    try {
      if (container.storage.ready) {
        const stealth = await container.storage.settings.get('stealth');
        const readReceipts = await container.storage.settings.get('readReceipts');
        const autoLockMinutes = await container.storage.settings.get('autoLockMinutes');
        const cacheLimitMB = await container.storage.settings.get('cacheLimitMB');
        const blockedWords = await container.storage.settings.get('blockedWords');
        const directOnly = await container.storage.settings.get('directOnly');
        const relayPreference = await container.storage.settings.get('relayPreference');
        set({
          stealth: typeof stealth === 'boolean' ? stealth : DEFAULT_STATE.stealth,
          readReceipts: typeof readReceipts === 'boolean' ? readReceipts : DEFAULT_STATE.readReceipts,
          autoLockMinutes: typeof autoLockMinutes === 'number' ? autoLockMinutes : DEFAULT_STATE.autoLockMinutes,
          cacheLimitMB: typeof cacheLimitMB === 'number' ? cacheLimitMB : DEFAULT_STATE.cacheLimitMB,
          blockedWords: Array.isArray(blockedWords) ? (blockedWords as string[]) : DEFAULT_STATE.blockedWords,
          directOnly: typeof directOnly === 'boolean' ? directOnly : DEFAULT_STATE.directOnly,
          relayPreference: (relayPreference as RelayPreference) ?? DEFAULT_STATE.relayPreference,
        });
      }
    } catch (err) {
      logger.warn('设置读取失败', { err });
    }
    set({ hydrated: true });
    get().theme === 'light' ? document.documentElement.setAttribute('data-theme', 'light') : document.documentElement.removeAttribute('data-theme');
  },

  setTheme: (theme) => {
    set({ theme });
    try {
      localStorage.setItem(LS_KEYS.theme, theme);
    } catch {
      // 忽略
    }
    if (theme === 'light') document.documentElement.setAttribute('data-theme', 'light');
    else document.documentElement.removeAttribute('data-theme');
    void persist('theme', theme);
  },

  toggleStealth: () => {
    const next = !get().stealth;
    set({ stealth: next });
    void persist('stealth', next);
  },

  setReadReceipts: (value) => {
    set({ readReceipts: value });
    void persist('readReceipts', value);
  },

  setAutoLockMinutes: (minutes) => {
    set({ autoLockMinutes: minutes });
    void persist('autoLockMinutes', minutes);
  },

  setCacheLimitMB: (mb) => {
    set({ cacheLimitMB: mb });
    void persist('cacheLimitMB', mb);
  },

  setBlockedWords: (words) => {
    set({ blockedWords: words });
    void persist('blockedWords', words);
  },

  setDirectOnly: (value) => {
    set({ directOnly: value });
    void persist('directOnly', value);
  },

  setRelayPreference: (value) => {
    set({ relayPreference: value });
    void persist('relayPreference', value);
  },

  reset: () => set({ ...DEFAULT_STATE }),
}));
